import { cn } from '@/lib/cn';
import useSize from '@react-hook/size';
import { Grid } from '@visx/grid';
import { Group } from '@visx/group';
import NoDataPlaceholder from 'components/NoDataPlaceholder';
import { DataStreamVisualisation } from 'dashboard-engine/types/Visualisation';
import { LegendWrapper } from 'dashboard-engine/visualisations/components/LegendWrapper';
import { NUM_Y_TICKS } from 'dashboard-engine/visualisations/getGridYValues';
import { useMemo, useRef, useState } from 'react';
import { useDrilldownContext } from 'ui/editor/dataStream/contexts/DrilldownContext';
import { DataStreamBarChartConfig, LegacyDataStreamBarChartConfig } from '../Config';
import { performLegacyBarChartColumnMigration } from '../performLegacyBarChartColumnMigration';
import { useColumns } from '../useColumns';
import { BarChartTooltip } from './BarChartTooltip';
import { BarTotals } from './BarTotals';
import { useAxisFormatters, useValueFormatter } from './format';
import { GroupedBars } from './GroupedBars';
import { StackedBars } from './StackedBars';
import { BarSegment, GroupedBar, StackedBar } from './types';
import { useBars } from './useBars';
import { XAxis } from './XAxis';
import { YAxis } from './YAxis';
import { getYAxisDimensions } from './yAxisDimensions';

const DataStreamBarChartVisx: DataStreamVisualisation<DataStreamBarChartConfig> = ({
    data,
    config: rawConfig,
    shapingConfig,
    onClick
}) => {
    // Perform migration from legacy bar chart configuration
    const config = performLegacyBarChartColumnMigration(rawConfig as LegacyDataStreamBarChartConfig, data);

    const displayMode = config?.displayMode ?? 'actual';
    const isPercentage = displayMode === 'percentage';
    const isHorizontal = config?.horizontalLayout === 'horizontal';
    const isGrouping = Boolean(config.grouping);
    const showLabels = Boolean(config.showValue);
    const showTotals = Boolean(config.showTotals) && !isGrouping && !isPercentage;
    const showGrid = config.showGrid ?? true;
    const isLegendVertical = config.legendPosition === 'top' || config.legendPosition === 'bottom';

    const graphRef = useRef<HTMLDivElement>(null);
    const graphWithLegendRef = useRef<HTMLDivElement>(null);

    const sizeRef = config.showLegend ? graphWithLegendRef : graphRef;
    const [width, height] = useSize(sizeRef);
    const [hoveredBar, setHoveredBar] = useState<BarSegment | undefined>(undefined);
    const [hoveredLegendId, setHoveredLegendId] = useState<string | undefined>(undefined);
    const { isDrilldownEnabled } = useDrilldownContext();

    const hoveredBarId = hoveredBar?.key ?? hoveredLegendId;
    const showBarChart = width > 0 && height > 0;

    const { series, dataKeys, valueColumn, axes, hasSeriesColumn } = useColumns({
        data,
        config,
        displayMode,
        isHorizontal,
        hasSorting: Boolean(shapingConfig?.sort?.by)
    });

    const categories = useMemo(() => series.map((d) => d.__group__ ?? ''), [series]);
    const xAxisColumnShape = axes?.x?.column?.shapeName;

    // Disable animation if there are too many bars to render
    const disableAnimation = series.length * dataKeys.length > 250;
    const grouped = isGrouping && hasSeriesColumn;

    const showXAxisLabel = config.showXAxisLabel ?? true;
    const showYAxisLabel = config.showYAxisLabel ?? true;

    // Swap axis label visibility and text for horizontal layout
    const xAxisLabelVisible = isHorizontal ? showYAxisLabel : showXAxisLabel;
    const yAxisLabelVisible = isHorizontal ? showXAxisLabel : showYAxisLabel;
    const xAxisLabel = xAxisLabelVisible ? axes?.x?.label : undefined;
    const yAxisLabel = yAxisLabelVisible ? axes?.y?.label : undefined;

    // Whether labels should be able to overflow the bar item to the right
    const overflowLabelsHorizontal =
        config.horizontalLayout === 'horizontal' && (!hasSeriesColumn || isGrouping) && !showTotals;
    // Whether labels should be able to overflow the bar item on top
    const overflowLabelsVertical =
        config.horizontalLayout === 'vertical' && (!hasSeriesColumn || isGrouping) && !showTotals;

    const overflowLabels = overflowLabelsHorizontal ? 'horizontal' : overflowLabelsVertical ? 'vertical' : undefined;

    const yAxisDimensions = getYAxisDimensions(isHorizontal, categories, width, yAxisLabelVisible);

    const { xAxisTickFormatter, yAxisTickFormatter } = useAxisFormatters({
        data,
        valueColumn,
        xAxisColumn: axes?.x?.column,
        yAxisColumn: axes?.y?.column,
        categories
    });

    const valueFormatter = useValueFormatter(valueColumn, data);

    const { bars, totals, categoryScale, valueScale, colorScale, xAxisTickPositions, rotateXAxisTicks, margin } =
        useBars({
            config,
            series,
            dataKeys,
            isHorizontal,
            isGrouping: grouped,
            isPercentage,
            showTotals,
            showLabels,
            showXAxisLabel: xAxisLabelVisible,
            showYAxisLabel: yAxisLabelVisible,
            xAxisColumnShape,
            yAxisWidth: yAxisDimensions.width,
            valueFormatter,
            xAxisTickFormatter,
            yAxisTickFormatter,
            width,
            height
        });

    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;

    const legend = dataKeys.map((dataKey) => ({
        id: dataKey,
        label: dataKey,
        color: colorScale(dataKey),
        isFocused: hoveredBarId !== undefined ? hoveredBarId === dataKey : true
    }));

    const xScale = isHorizontal ? valueScale : categoryScale;
    const yScale = isHorizontal ? categoryScale : valueScale;

    if (!series || series.length === 0) {
        return <NoDataPlaceholder />;
    }

    if (series.every((s) => s.value === 0)) {
        return <NoDataPlaceholder message='No non-zero values to display.' />;
    }

    const barChartProps = {
        overflowLabels: overflowLabels as 'horizontal' | 'vertical' | undefined,
        disableAnimation,
        showLabels,
        allowLink: isDrilldownEnabled,
        hoveredBarId,
        setHoveredBar
    };

    const barChart = (
        <div
            data-visualization='data-stream-bar-chart'
            className={cn('absolute inset-0 font-inter', onClick && 'cursor-pointer')}
            ref={graphRef}
            onClick={onClick}
        >
            <svg width={width} height={height}>
                {showGrid && (
                    <Grid
                        numTicksRows={isHorizontal ? undefined : NUM_Y_TICKS}
                        numTicksColumns={0}
                        columnTickValues={isHorizontal ? [0] : []}
                        rowTickValues={isHorizontal ? [] : undefined}
                        top={margin.top}
                        left={margin.left}
                        xScale={xScale}
                        yScale={yScale}
                        width={innerWidth}
                        height={innerHeight}
                        stroke='var(--dividerTertiary)'
                    />
                )}
                <YAxis
                    scale={yScale}
                    yAxisTickFormatter={yAxisTickFormatter}
                    marginTop={margin.top}
                    marginLeft={margin.left}
                    isHorizontal={isHorizontal}
                    label={yAxisLabel}
                />
                <XAxis
                    scale={xScale}
                    height={height}
                    marginLeft={margin.left}
                    marginBottom={margin.bottom}
                    label={xAxisLabel}
                    isHorizontal={isHorizontal}
                    tickPositions={xAxisTickPositions}
                    rotateTicks={rotateXAxisTicks}
                />

                <Group top={margin.top} left={margin.left}>
                    {grouped ? (
                        <GroupedBars bars={bars as GroupedBar[]} {...barChartProps} />
                    ) : (
                        <StackedBars bars={bars as StackedBar[]} {...barChartProps} />
                    )}
                    {showTotals && <BarTotals totals={totals} isHorizontal={isHorizontal} />}
                </Group>
            </svg>
            {hoveredBar && (
                <BarChartTooltip
                    hoveredBar={hoveredBar}
                    graphRef={graphRef}
                    displayMode={displayMode}
                    xAxisColumnShape={xAxisColumnShape}
                    valueFormatter={valueFormatter}
                    xAxisTickFormatter={xAxisTickFormatter}
                />
            )}
        </div>
    );

    if (config.showLegend && config.legendPosition) {
        return (
            <LegendWrapper
                legend={legend}
                position={config.legendPosition}
                containerSizePercentage={isLegendVertical ? 0.3 : 0.25}
                legendContainerClassname='p-5'
                vizRef={graphWithLegendRef}
                onHover={(properties) => setHoveredLegendId(properties.id)}
                onLeave={() => setHoveredLegendId(undefined)}
            >
                <div ref={graphWithLegendRef} className='relative w-full h-full overflow-hidden'>
                    {showBarChart ? barChart : null}
                </div>
            </LegendWrapper>
        );
    }

    return (
        <div ref={graphRef} className='relative w-full h-full overflow-hidden'>
            {showBarChart ? barChart : null}
        </div>
    );
};

export default DataStreamBarChartVisx;
